import { useCallback } from 'react';

import { Auth0ProviderConfig } from '../config/types';
import { openPopup } from '../utils';
import { type ConnectedAccount } from './accountLinkHelpers';
import { useAuth0AccountLink } from './useAuth0AccountLink';
import { useConnectionCheck } from './useConnectionCheck';

type LinkResult = Promise<{
  linked: boolean;
  failureReason?: string;
  failureDebugMessage?: string;
}>;

export type UseProviderConnectResult = {
  isConnected: boolean;
  isCheckingConnection: boolean;
  connect: () => LinkResult;
  reconnect: () => LinkResult;
  connectedAccount: ConnectedAccount | undefined;
  unlinkAccount: () => Promise<void>;
  clearAuthCache: () => Promise<void>;
};

export const useProviderConnect = (
  config: Auth0ProviderConfig,
  options: { isDialogOpen?: boolean } = {},
): UseProviderConnectResult => {
  const { isDialogOpen = true } = options;
  const {
    linkAccount,
    isConnected,
    checkConnection,
    connectedAccount,
    unlinkAccount,
    clearAuthCache,
  } = useAuth0AccountLink(config);

  const isCheckingConnection = useConnectionCheck({
    checkConnection,
    isConnected,
    isDialogOpen,
  });

  const connect = useCallback(
    () => linkAccount({ popup: openPopup() }),
    [linkAccount],
  );

  const reconnect = useCallback(
    () => linkAccount({ popup: openPopup(), forceRelink: true }),
    [linkAccount],
  );

  return {
    isConnected,
    isCheckingConnection,
    connect,
    reconnect,
    connectedAccount,
    unlinkAccount,
    clearAuthCache,
  };
};
